import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from './ThemeProvider';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { dialogs } from '../context/DialogContext';
import { IconoWhatsAppHistorial } from './historial/IconoWhatsAppHistorial';
import { IconoLlamadaHistorial } from './historial/IconoLlamadaHistorial';
import { TASK_UI } from '../constants/taskUiColors';
import CitaSelectorHora from './cita/CitaSelectorHora';

function IconoCita({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function IconoRecordatorio({ size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15 14" />
    </svg>
  );
}

function tileIcon(bg, sh, ic) {
  return (
    <div style={{
      width: 30, height: 30, borderRadius: 6,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: bg, boxShadow: sh, flexShrink: 0,
    }}>
      {ic}
    </div>
  );
}

const PRESETS = [
  {
    id: 'llamar',
    label: 'Llamar',
    tipo: 'llamar',
    desc: (n) => `Llamar a ${n}`,
    color: TASK_UI.llamada,
    renderIcon: () => tileIcon(
      TASK_UI.llamada,
      '0 2px 6px rgba(255,222,33,0.4)',
      <IconoLlamadaHistorial size={22} />
    ),
  },
  {
    id: 'whatsapp',
    label: 'Enviar Was',
    tipo: 'whatsapp',
    desc: (n) => `Enviar WhatsApp a ${n}`,
    color: TASK_UI.whatsapp,
    renderIcon: () => tileIcon(
      TASK_UI.whatsapp,
      '0 2px 6px rgba(37,211,102,0.35)',
      <IconoWhatsAppHistorial size={15} />
    ),
  },
  {
    id: 'recordatorio',
    label: 'Recordatorio',
    tipo: 'seguimiento',
    desc: (n) => `Recordatorio: recontactar a ${n}`,
    color: '#f59e0b',
    renderIcon: () => tileIcon(
      'linear-gradient(135deg, #f59e0b, #d97706)',
      '0 2px 6px rgba(245,158,11,0.35)',
      <IconoRecordatorio size={15} />
    ),
  },
  {
    id: 'visitar',
    label: 'Visitar',
    tipo: 'visita',
    desc: (n) => `Visitar / pasar por el inmueble de ${n}`,
    color: TASK_UI.cita,
    renderIcon: () => tileIcon(
      TASK_UI.cita,
      '0 2px 6px rgba(37,99,235,0.35)',
      <IconoCita size={15} />
    ),
  },
];

const PRIORIDADES = [
  { id: 'normal', label: 'Normal', color: '#64748b' },
  { id: 'alta', label: 'Alta', color: '#f97316' },
  { id: 'urgente', label: 'Urgente', color: '#ef4444' },
];

function pad(n) {
  return String(n).padStart(2, '0');
}

/** Fecha local YYYY-MM-DD */
function fechaLocal(d = new Date()) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function horaLocal(d = new Date()) {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function addDays(base, days) {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d;
}

function addMonths(base, months) {
  const d = new Date(base);
  d.setMonth(d.getMonth() + months);
  return d;
}

function toIsoFromLocal(fecha, hora) {
  if (!fecha || !hora) return null;
  const [y, m, day] = fecha.split('-').map(Number);
  const [hh, mm] = hora.split(':').map(Number);
  const d = new Date(y, m - 1, day, hh || 0, mm || 0, 0, 0);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function partsFromIso(iso) {
  if (!iso) return { fecha: '', hora: '10:00' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { fecha: '', hora: '10:00' };
  return { fecha: fechaLocal(d), hora: horaLocal(d) };
}

function presetIdFromTipo(tipo, meta = {}) {
  if (meta.preset) return meta.preset;
  const t = String(tipo || '').toLowerCase();
  if (t === 'whatsapp') return 'whatsapp';
  if (t === 'visita' || t === 'agendar_visita') return 'visitar';
  if (t === 'seguimiento') return 'recordatorio';
  if (t === 'llamar' || t === 'llamada' || t === 'contactar') return 'llamar';
  return 'llamar';
}

/**
 * Crear o editar tarea manual vinculada a una captación.
 * fecha_inicio = cuándo aparece / empieza; fecha_vencimiento = plazo.
 */
export default function ModalCrearTareaCaptacion({
  isOpen,
  captacion,
  tarea = null,
  onClose,
  onCreada,
  onGuardada,
}) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === 'dark';
  const { usuario } = useAuth();
  const esEdicion = Boolean(tarea?.id);

  const nombreProp = useMemo(() => {
    if (captacion?.nombre || captacion?.apellido) {
      return [captacion.nombre, captacion.apellido].filter(Boolean).join(' ') || 'propietario';
    }
    return String(tarea?.captacion_nombre || '').trim() || 'propietario';
  }, [captacion?.nombre, captacion?.apellido, tarea?.captacion_nombre]);

  const captacionId = captacion?.id || tarea?.captacion_id || null;

  const [presetId, setPresetId] = useState('llamar');
  const [tipo, setTipo] = useState('llamar');
  const [descripcion, setDescripcion] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [horaInicio, setHoraInicio] = useState('10:00');
  const [fechaVence, setFechaVence] = useState('');
  const [horaVence, setHoraVence] = useState('10:00');
  const [vencePreset, setVencePreset] = useState('manana');
  const [prioridad, setPrioridad] = useState('alta');
  const [enHistorial, setEnHistorial] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setGuardando(false);
    setError('');

    if (tarea?.id) {
      let meta = {};
      try {
        meta = typeof tarea.metadata === 'object' && tarea.metadata
          ? tarea.metadata
          : JSON.parse(tarea.metadata || '{}');
      } catch {
        meta = {};
      }
      const pid = presetIdFromTipo(tarea.tipo, meta);
      setPresetId(pid);
      setTipo(String(tarea.tipo || 'llamar').toLowerCase());
      setDescripcion(String(tarea.descripcion || '').trim());
      const ini = partsFromIso(tarea.fecha_inicio);
      const ven = partsFromIso(tarea.fecha_vencimiento);
      setFechaInicio(ini.fecha);
      setHoraInicio(ini.hora);
      setFechaVence(ven.fecha || ini.fecha);
      setHoraVence(ven.hora || ini.hora);
      setVencePreset(meta.schedule_preset || null);
      setPrioridad(String(tarea.prioridad || 'alta').toLowerCase());
      setEnHistorial(false);
      return;
    }

    const now = new Date();
    const manana = addDays(now, 1);
    setPresetId('llamar');
    setTipo('llamar');
    setDescripcion(`Llamar a ${nombreProp}`);
    setFechaInicio(fechaLocal(now));
    setHoraInicio(horaLocal(now));
    setFechaVence(fechaLocal(manana));
    setHoraVence('18:00');
    setVencePreset(null);
    setPrioridad('alta');
    setEnHistorial(true);
  }, [isOpen, nombreProp, tarea]);

  if (!isOpen) return null;
  if (!esEdicion && !captacionId) return null;

  const aplicarPreset = (p) => {
    setPresetId(p.id);
    setTipo(p.tipo);
    setDescripcion(p.desc(nombreProp));
  };

  /** Presets: inicio = cuándo aparece; vence = plazo (mismo momento salvo «Hoy»). */
  const aplicarVence = (kind) => {
    const now = new Date();
    setVencePreset(kind);
    if (kind === 'hoy') {
      const in2h = new Date(now.getTime() + 2 * 3600000);
      setFechaInicio(fechaLocal(now));
      setHoraInicio(horaLocal(now));
      setFechaVence(fechaLocal(in2h));
      setHoraVence(horaLocal(in2h));
      return;
    }
    let target = null;
    if (kind === 'manana') target = addDays(now, 1);
    else if (kind === '2d') target = addDays(now, 2);
    else if (kind === '1s') target = addDays(now, 7);
    else if (kind === '6m') target = addMonths(now, 6);
    if (!target) return;
    const f = fechaLocal(target);
    setFechaInicio(f);
    setHoraInicio('10:00');
    setFechaVence(f);
    setHoraVence('10:00');
  };

  const guardar = async () => {
    const texto = String(descripcion || '').trim();
    if (!texto) {
      setError('Escribe qué hay que hacer');
      return;
    }
    const inicioIso = toIsoFromLocal(fechaInicio, horaInicio);
    const vencimiento = toIsoFromLocal(fechaVence, horaVence);
    if (!inicioIso || !vencimiento) {
      setError('Indica fecha y hora de inicio y de vencimiento');
      return;
    }
    if (new Date(vencimiento).getTime() < new Date(inicioIso).getTime()) {
      setError('El vencimiento no puede ser anterior al inicio');
      return;
    }
    if (new Date(vencimiento).getTime() < Date.now() - 60_000) {
      setError('La fecha de vencimiento no puede ser en el pasado');
      return;
    }

    setGuardando(true);
    setError('');
    try {
      const preset = PRESETS.find((p) => p.id === presetId);

      if (esEdicion) {
        let metaPrev = {};
        try {
          metaPrev = typeof tarea.metadata === 'object' && tarea.metadata
            ? tarea.metadata
            : JSON.parse(tarea.metadata || '{}');
        } catch {
          metaPrev = {};
        }
        const { data } = await api.patch(`/tareas/${tarea.id}`, {
          tipo: tipo || 'contactar',
          descripcion: texto,
          prioridad: prioridad || 'alta',
          fecha_inicio: inicioIso,
          fecha_vencimiento: vencimiento,
          metadata: JSON.stringify({
            ...metaPrev,
            source: metaPrev.source || 'modal_crear_tarea_captacion',
            preset: presetId || null,
            preset_label: preset?.label || null,
            schedule_preset: vencePreset || null,
          }),
        });
        const updated = data?.data || null;
        if (enHistorial && captacionId) {
          await api.post(`/captaciones/${captacionId}/historial`, {
            tipo: 'sistema',
            contenido: `Tarea actualizada: ${texto}`,
            metadata: {
              origen: 'editar_tarea_manual',
              tarea_id: tarea.id,
              tipo_tarea: tipo,
            },
          }).catch(() => {});
        }
        onGuardada?.(updated);
        onClose?.();
        return;
      }

      const agenteId = captacion?.agente_id || usuario?.agente_id || null;
      const { data } = await api.post('/tareas', {
        captacion_id: captacionId,
        agente_id: agenteId,
        asignada_a: 'agente',
        tipo: tipo || 'contactar',
        descripcion: texto,
        prioridad: prioridad || 'alta',
        fecha_inicio: inicioIso,
        fecha_vencimiento: vencimiento,
        origen: 'manual_captacion',
        metadata: {
          source: 'modal_crear_tarea_captacion',
          preset: presetId || null,
          preset_label: preset?.label || null,
          schedule_preset: vencePreset || null,
        },
      });

      if (enHistorial && captacionId) {
        await api.post(`/captaciones/${captacionId}/historial`, {
          tipo: 'sistema',
          contenido: `Tarea creada: ${texto}`,
          metadata: {
            origen: 'crear_tarea_manual',
            tarea_id: data?.data?.id || null,
            tipo_tarea: tipo,
          },
        }).catch(() => {});
      }

      onCreada?.(data?.data || null);
      onClose?.();
    } catch (e) {
      console.error('[ModalCrearTareaCaptacion]', e);
      setError(e?.response?.data?.error || e.message || 'No se pudo guardar la tarea');
      dialogs.alert(e?.response?.data?.error || 'Error al guardar la tarea');
    } finally {
      setGuardando(false);
    }
  };

  const inp = {
    width: '100%',
    padding: '8px 10px',
    borderRadius: 8,
    border: `1px solid ${c.border}`,
    background: c.bgInput,
    color: c.textPrimary,
    fontSize: 13,
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  };
  const lbl = {
    fontSize: 11,
    fontWeight: 600,
    color: c.textMuted,
    display: 'block',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  };

  const chipVence = (id, label) => {
    const on = vencePreset === id;
    return (
      <button
        key={id}
        type="button"
        onClick={() => aplicarVence(id)}
        style={{
          padding: '5px 10px',
          borderRadius: 8,
          border: `1.5px solid ${on ? '#3b82f6' : c.border}`,
          background: on ? 'rgba(59,130,246,0.16)' : c.bgInput,
          color: on ? '#3b82f6' : c.textSecondary,
          fontSize: 11,
          fontWeight: 700,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        {label}
      </button>
    );
  };

  const telefono = captacion?.telefono || tarea?.captacion_telefono || '';

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 11000,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 440,
          maxHeight: '92vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          background: c.bgCard || c.bgPanel,
          borderRadius: 14,
          border: `1px solid ${c.border}`,
          boxShadow: isDark
            ? '0 20px 60px rgba(0,0,0,0.55)'
            : '0 16px 48px rgba(0,0,0,0.25)',
          fontFamily: "'DM Sans', sans-serif",
          boxSizing: 'border-box',
        }}
      >
        <div style={{
          padding: '14px 16px',
          borderBottom: `1px solid ${c.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: c.textPrimary }}>
              {esEdicion ? 'Editar tarea' : 'Crear tarea'}
            </div>
            <div style={{ fontSize: 12, color: c.textMuted, marginTop: 2 }}>
              {nombreProp}
              {telefono ? ` · ${telefono}` : ''}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              color: c.textMuted,
              fontSize: 20,
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={lbl}>Tipo rápido</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {PRESETS.map((p) => {
                const on = presetId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => aplicarPreset(p)}
                    style={{
                      padding: '10px 8px',
                      borderRadius: 10,
                      border: `1.5px solid ${on ? p.color : c.border}`,
                      background: on ? `${p.color}18` : (c.btnActionBg || c.bgInput),
                      color: on ? (p.id === 'llamar' || p.id === 'whatsapp' ? '#111' : p.color) : (c.btnActionText || c.textSecondary),
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: 12,
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                    }}
                  >
                    {p.renderIcon(c)}
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label style={lbl}>Qué hay que hacer</label>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              rows={3}
              placeholder="Describe la tarea…"
              style={{ ...inp, resize: 'vertical', minHeight: 72 }}
            />
          </div>

          <div>
            <label style={lbl}>Cuándo</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {chipVence('hoy', 'Hoy (+2h)')}
              {chipVence('manana', 'Mañana 10:00')}
              {chipVence('2d', 'En 2 días')}
              {chipVence('1s', 'En 1 semana')}
              {chipVence('6m', 'En 6 meses')}
            </div>
            <div style={{ fontSize: 11, color: c.textMuted, marginBottom: 8, lineHeight: 1.35 }}>
              No aparece en Tareas hasta la fecha de inicio. Un recordatorio a 6 meses no satura el tablero antes.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
              <div style={{ minWidth: 0 }}>
                <label style={{ ...lbl, marginBottom: 4 }}>Inicio</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(108px, 0.9fr)', gap: 6, alignItems: 'end' }}>
                  <input
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFechaInicio(v);
                      setVencePreset(null);
                      if (fechaVence && v > fechaVence) {
                        setFechaVence(v);
                      }
                    }}
                    style={{ ...inp, minWidth: 0 }}
                  />
                  <CitaSelectorHora
                    value={horaInicio || '10:00'}
                    onChange={(v) => {
                      setHoraInicio(v);
                      setVencePreset(null);
                    }}
                    c={c}
                    isDark={isDark}
                    accentColor="#3b82f6"
                    accentBg="rgba(59,130,246,0.15)"
                    chipText={isDark ? '#93c5fd' : '#1d4ed8'}
                    borderColor={c.border}
                    align="right"
                    zIndex={12050}
                  />
                </div>
              </div>
              <div style={{ minWidth: 0 }}>
                <label style={{ ...lbl, marginBottom: 4 }}>Vence</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(108px, 0.9fr)', gap: 6, alignItems: 'end' }}>
                  <input
                    type="date"
                    value={fechaVence}
                    onChange={(e) => {
                      setFechaVence(e.target.value);
                      setVencePreset(null);
                    }}
                    style={{ ...inp, minWidth: 0 }}
                  />
                  <CitaSelectorHora
                    value={horaVence || '12:00'}
                    onChange={(v) => {
                      setHoraVence(v);
                      setVencePreset(null);
                    }}
                    c={c}
                    isDark={isDark}
                    accentColor="#3b82f6"
                    accentBg="rgba(59,130,246,0.15)"
                    chipText={isDark ? '#93c5fd' : '#1d4ed8'}
                    borderColor={c.border}
                    align="right"
                    zIndex={12050}
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            <label style={lbl}>Prioridad</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {PRIORIDADES.map((p) => {
                const on = prioridad === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPrioridad(p.id)}
                    style={{
                      flex: 1,
                      padding: '7px 6px',
                      borderRadius: 8,
                      border: `1.5px solid ${on ? p.color : c.border}`,
                      background: on ? `${p.color}18` : c.bgInput,
                      color: on ? p.color : c.textSecondary,
                      fontSize: 11,
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {captacionId && (
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12,
              color: c.textSecondary,
              cursor: 'pointer',
              userSelect: 'none',
            }}>
              <input
                type="checkbox"
                checked={enHistorial}
                onChange={(e) => setEnHistorial(e.target.checked)}
                style={{ accentColor: '#3b82f6' }}
              />
              Dejar constancia en el historial de la captación
            </label>
          )}

          {error && (
            <div style={{
              fontSize: 12,
              color: '#ef4444',
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.35)',
              borderRadius: 8,
              padding: '8px 10px',
            }}>
              {error}
            </div>
          )}
        </div>

        <div style={{
          padding: '12px 16px 16px',
          borderTop: `1px solid ${c.border}`,
          display: 'flex',
          gap: 8,
        }}>
          <button
            type="button"
            onClick={onClose}
            disabled={guardando}
            style={{
              flex: 1,
              padding: '10px',
              borderRadius: 9,
              border: `1px solid ${c.border}`,
              background: c.bgInput,
              color: c.textSecondary,
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={guardar}
            disabled={guardando}
            style={{
              flex: 1.4,
              padding: '10px',
              borderRadius: 9,
              border: 'none',
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              color: '#fff',
              fontWeight: 700,
              fontSize: 13,
              cursor: guardando ? 'wait' : 'pointer',
              fontFamily: 'inherit',
              opacity: guardando ? 0.7 : 1,
            }}
          >
            {guardando ? 'Guardando…' : (esEdicion ? 'Guardar cambios' : 'Crear tarea')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
